import { Button, Checkbox, Group, Modal, Pagination, Space, Table, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import { modelFilterHook } from '../../../../../shared/utils/modelFilter';
import { useUnDispatched } from './hooks/useUnDispatched';
import { useLocation } from 'react-router-dom';
import { IconReload } from '@tabler/icons-react';
import { useSearchInput } from './hooks/useSearchInput';
import { IDevice } from '../Register/types';

interface IPropsDeviceListModal {
  opened: boolean;
  close: () => void;
  initialRows: IDevice[];
  onConfirm: (rows: IDevice[]) => void;
  fromCompanyId: number | null;
}

export default function DeviceListModal(props: IPropsDeviceListModal) {
  const { opened, close, initialRows, onConfirm, fromCompanyId } = props;
  const location = useLocation();
  const isEptp = location.pathname.includes('eptp');
  const isManufacturer = location.pathname.includes('manufacturer');
  const { inputRef, searchTerm, onSearch, onClear } = useSearchInput();
  const [page, setPage] = useState<number>(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set(initialRows.map((r) => r.id)));
  const [selectedMap, setSelectedMap] = useState(() => new Map(initialRows.map((r) => [r.id, r])));
  const size = 10;
  const payload = {
    page,
    size,
    channel: isEptp ? 'MFM' : isManufacturer ? 'SCAUTR' : 'PROGIX',
    mac: searchTerm,
    companyId: fromCompanyId,
  };
  const { unDispatchedList, totalCount } = useUnDispatched(payload);
  const pageIds = unDispatchedList.map((d) => d.id);
  const isAllCheckedOnPage = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const onSelectRow = (row: IDevice) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  };

  const onSelectAllRows = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllCheckedOnPage) {
        // 현재 페이지 것만 해제
        pageIds.forEach((id) => next.delete(id));
      } else {
        // 현재 페이지 것만 추가
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });

    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (isAllCheckedOnPage) {
        // 현재 페이지 것만 해제
        pageIds.forEach((id) => next.delete(id));
      } else {
        // 현재 페이지 것만 추가(머지)
        unDispatchedList.forEach((d) => next.set(d.id, d));
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedMap.values()));
  };

  return (
    <Modal title="MAC 번호 조회" centered opened={opened} onClose={close} size="lg">
      <Group mb="sm">
        <TextInput ref={inputRef} placeholder="MAC번호를 검색해주세요." />
        <Button
          onClick={() => {
            setPage(1);
            onSearch();
          }}
        >
          검색
        </Button>
        <UnstyledButton
          onClick={onClear}
          style={{
            cursor: 'pointer',
          }}
        >
          <IconReload size={16} />
          <Text size="sm" ml={4}>
            초기화
          </Text>
        </UnstyledButton>
      </Group>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Checkbox
                styles={{
                  input: {
                    cursor: 'pointer',
                  },
                  body: {
                    display: 'flex',
                    justifyContent: 'center',
                  },
                }}
                checked={isAllCheckedOnPage}
                onChange={onSelectAllRows}
              />
            </Table.Th>
            <Table.Th>MAC번호</Table.Th>
            <Table.Th>디바이스 종류</Table.Th>
            <Table.Th>S/N</Table.Th>
            <Table.Th>메모</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {unDispatchedList?.map((device) => (
            <Table.Tr key={device.id}>
              <Table.Td>
                <Checkbox
                  styles={{
                    input: {
                      cursor: 'pointer',
                    },
                    body: {
                      display: 'flex',
                      justifyContent: 'center',
                    },
                  }}
                  checked={selectedIds.has(device.id)}
                  onChange={() => onSelectRow(device)}
                />
              </Table.Td>
              <Table.Td>{device.mac}</Table.Td>
              <Table.Td>{modelFilterHook(device.deviceTypeCd, device.deviceSubTypeCd)}</Table.Td>
              <Table.Td>{device.serialNumber}</Table.Td>
              <Table.Td>{device.memo}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group justify="center" mt="lg">
        <Pagination total={Math.ceil(totalCount / size)} value={page} onChange={setPage} />
      </Group>
      <Space h="lg" />
      <Group justify="center">
        <Button variant="outline" onClick={close}>
          취소
        </Button>
        <Button
          color="secondary.5"
          onClick={() => {
            handleConfirm();
            close();
          }}
        >
          확인
        </Button>
      </Group>
    </Modal>
  );
}
